'use client';

import { useEffect, useRef } from 'react';
import { useLocale } from 'next-intl';
import { getAllTieReferences } from '@/lib/tie-quotes';
import { fetchMultipleVerses, type FetchedVerse } from '@/lib/bible-api';

const STORAGE_KEY = 'love_languages_verses_cache';
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface CachedVerses {
  version: number;
  locale: string;
  timestamp: number;
  verses: Record<string, string>;
}

function isCacheValid(cache: CachedVerses, locale: string): boolean {
  if (cache.version !== CACHE_VERSION) return false;
  if (cache.locale !== locale) return false;
  if (Date.now() - cache.timestamp > CACHE_TTL_MS) return false;
  return true;
}

function getCache(locale: string): CachedVerses | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const cache: CachedVerses = JSON.parse(stored);
    return isCacheValid(cache, locale) ? cache : null;
  } catch {
    return null;
  }
}

function setCache(locale: string, verses: FetchedVerse[]): void {
  try {
    const cache: CachedVerses = {
      version: CACHE_VERSION,
      locale,
      timestamp: Date.now(),
      verses: Object.fromEntries(verses.map((v) => [v.reference, v.text])),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.error('[Bible Prefetch] Failed to cache verses:', e);
  }
}

export function useBiblePrefetch(): void {
  const locale = useLocale();
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;

    const existingCache = getCache(locale);
    if (existingCache) {
      console.log('[Bible Prefetch] Using cached verses');
      return;
    }

    hasFetched.current = true;

    const prefetch = async () => {
      try {
        const references = getAllTieReferences();
        console.log(`[Bible Prefetch] Fetching ${references.length} verses for ${locale}`);
        const verses = await fetchMultipleVerses(references, locale);
        setCache(locale, verses);
        console.log(`[Bible Prefetch] Cached ${verses.length} verses`);
      } catch (e) {
        console.error('[Bible Prefetch] Error:', e);
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => prefetch(), { timeout: 5000 });
    } else {
      setTimeout(prefetch, 2000);
    }
  }, [locale]);
}

export function getCachedVerse(reference: string, locale: string): string | null {
  const cache = getCache(locale);
  return cache?.verses[reference] ?? null;
}
